import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { hotComics, testimonials, faqItems } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  const comic = useMemo(() => hotComics.find((c) => c.id === id), [id]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-400 mb-4 block"></i>
            <p className="text-foreground-600 text-lg mb-4">未找到该漫画</p>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回分类
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const tagLabel = (tagKey: string) => {
    const map: Record<string, string> = {};
    for (const t of [
      { key: 'romance', label: '恋爱' },
      { key: 'suspense', label: '悬疑' },
      { key: 'action', label: '动作' },
      { key: 'fantasy', label: '奇幻' },
      { key: 'comedy', label: '喜剧' },
      { key: 'drama', label: '剧情' },
      { key: 'thriller', label: '惊悚' },
      { key: 'school', label: '校园' },
    ]) {
      map[t.key] = t.label;
    }
    return map[tagKey] || tagKey;
  };

  return (
    <>
      <Navbar />
      <main>
        <article>
          <section className="pt-20 pb-10 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8" aria-label="面包屑导航">
                <Link to="/" className="hover:text-foreground-700 transition-colors">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                <Link
                  to="/category"
                  className="hover:text-foreground-700 transition-colors"
                >
                  全部分类
                </Link>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
                <span className="text-foreground-800 font-medium">
                  {comic.title}
                </span>
              </nav>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {comic.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/category?tag=${tag}`}
                        className="bg-secondary-100 text-secondary-700 text-xs px-2.5 py-1 rounded-full hover:bg-secondary-200 transition-colors"
                      >
                        {tagLabel(tag)}
                      </Link>
                    ))}
                    <span
                      className={`text-xs px-2.5 py-1 rounded-full ${
                        comic.status === '连载中'
                          ? 'bg-accent-100 text-accent-700'
                          : 'bg-background-200 text-foreground-600'
                      }`}
                    >
                      {comic.status}
                    </span>
                  </div>

                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-900 leading-tight">
                    {comic.title}
                  </h1>

                  <div className="flex items-center gap-4 mt-4 text-sm text-foreground-600">
                    <span>作者：{comic.author}</span>
                    <span className="text-accent-500">★ {comic.rating}</span>
                    <span>
                      {comic.views >= 1000000
                        ? `${(comic.views / 1000000).toFixed(1)}M 阅读`
                        : `${(comic.views / 10000).toFixed(0)}万 阅读`}
                    </span>
                  </div>

                  <p className="mt-5 text-foreground-700 leading-relaxed text-sm md:text-base">
                    {comic.description}
                  </p>

                  <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <Link
                      to={`/comic/${comic.id}/read`}
                      className="group inline-flex items-center justify-center gap-3 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3.5 rounded-lg text-sm font-semibold transition-all duration-300 whitespace-nowrap"
                    >
                      开始阅读
                      <span className="w-7 h-7 border border-background-50/40 rounded flex items-center justify-center">
                        <i className="ri-arrow-right-line text-sm group-hover:translate-x-0.5 transition-transform"></i>
                      </span>
                    </Link>
                    <span className="text-foreground-500 text-xs">
                      共 {comic.chapterCount} 话 · 最后更新 {comic.updateDate}
                    </span>
                  </div>
                </div>

                <div className="flex justify-center lg:justify-end">
                  <img
                    src={comic.coverUrl}
                    alt={comic.title}
                    title={`${comic.title} - 韩漫网`}
                    className="w-full max-w-sm rounded-lg object-cover aspect-[3/4]"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="relative h-[500px] md:h-[600px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20reading%20atmosphere%2C%20warm%20amber%20lighting%2C%20cozy%20reading%20nook%2C%20soft%20focus%20background%2C%20gentle%20natural%20light%2C%20warm%20creamy%20tones%2C%20peaceful%20ambient%20interior%2C%20artistic%20photography%20with%20shallow%20depth%20of%20field&width=1600&height=900&seq=reading-bg&orientation=landscape"
              alt="阅读氛围"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-primary-900/60" />
            <div className="relative z-10 text-center px-4">
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-background-50 tracking-wide uppercase">
                START READING
              </h2>
              <p className="mt-4 text-background-50/70 text-sm md:text-base max-w-md mx-auto leading-relaxed">
                无广告纯净模式 / 极速流畅翻页体验
              </p>
              <Link
                to={`/comic/${comic.id}/read`}
                className="mt-8 inline-flex items-center gap-3 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap"
              >
                <span className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center">
                  <i className="ri-book-open-line text-background-50 text-sm"></i>
                </span>
                立即阅读
                <i className="ri-arrow-right-up-line text-lg"></i>
              </Link>
            </div>
          </section>

          <section className="bg-background-100 py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="mb-10">
                <span className="inline-block bg-secondary-100 text-secondary-700 text-xs font-medium px-3 py-1 rounded-full">
                  读者评价
                </span>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mt-4">
                  真实读者反馈
                </h2>
              </div>

              <div className="columns-1 md:columns-2 lg:columns-3 gap-5">
                {testimonials.slice(0, 3).map((item) => (
                  <div
                    key={item.id}
                    className="break-inside-avoid bg-background-50 rounded-lg p-5 mb-5"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={item.avatar}
                        alt={item.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-foreground-900 font-semibold text-sm">
                          {item.name}
                        </p>
                        <p className="text-foreground-500 text-xs">
                          {item.username}
                        </p>
                      </div>
                    </div>
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      &ldquo;{item.content}&rdquo;
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="bg-background-50 py-16 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 text-center mb-10">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-background-100 rounded-lg overflow-hidden"
                  >
                    <button
                      type="button"
                      className="w-full flex items-center justify-between px-5 py-4 text-left"
                      onClick={() => setFaqOpen(faqOpen === idx ? null : idx)}
                    >
                      <span className="text-foreground-900 font-medium text-sm pr-4">
                        {item.question}
                      </span>
                      <i
                        className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 flex-shrink-0 ${
                          faqOpen === idx ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    {faqOpen === idx && (
                      <div className="px-5 pb-4">
                        <p className="text-foreground-600 text-sm leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${comic.title} - 韩漫网`,
            description: comic.description,
            image: comic.coverUrl,
            author: {
              '@type': 'Person',
              name: comic.author,
            },
            datePublished: '2025-01-01',
            dateModified: comic.updateDate,
            publisher: {
              '@type': 'Organization',
              name: '韩漫网',
              url: 'http://bwpntxx.cn/',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `http://bwpntxx.cn/comic/${comic.id}`,
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'http://bwpntxx.cn/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: '全部分类',
                  item: 'http://bwpntxx.cn/category',
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: comic.title,
                  item: `http://bwpntxx.cn/comic/${comic.id}`,
                },
              ],
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          }),
        }}
      />
    </>
  );
}